import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { Placement } from 'react-bootstrap/types';
import classNames from 'classnames';

type TriggerChildProps = Partial<
  Pick<HTMLAttributes<HTMLElement>, 'onMouseEnter' | 'onMouseLeave' | 'className'>
>;

export interface ChildrensTooltipProps {
  html?: ReactNode;
  color?: string;
  delayToShow?: number;
  children: ReactNode;
  delayToRemove?: number;
  className?: string;
  placement?: Placement;
  showOnHover?: boolean;
  noChildSpan?: boolean;
}

const ChildrensTooltip = ({
  html,
  delayToShow = 0,
  children,
  delayToRemove = 650,
  className,
  placement = 'auto',
  showOnHover = true,
  noChildSpan = false
}: ChildrensTooltipProps) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShouldShow(true);
    }, delayToShow);
  };

  const handleMouseLeave = () => {
    setIsTooltipHovered(false);
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setShouldShow(false);
      }
    }, delayToRemove);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    };
  }, []);

  const childProps: TriggerChildProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    className
  };

  return (
    <OverlayTrigger
      show={!!html && shouldShow}
      placement={placement}
      overlay={
        <Tooltip
          onClick={e => e.stopPropagation()}
          onMouseEnter={() => {
            if (showOnHover) {
              if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
              setIsTooltipHovered(true);
            }
          }}
          onMouseLeave={() => {
            setIsTooltipHovered(false);
            if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
            setShouldShow(false);
          }}
          style={{ position: 'fixed' }}
          className={classNames('text-danger')}
        >
          {html}
        </Tooltip>
      }
    >
      {noChildSpan ? (
        isValidElement(children) ? (
          cloneElement(children as ReactElement<TriggerChildProps>, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            className: classNames(
              (children as ReactElement<TriggerChildProps>).props.className,
              className
            )
          })
        ) : (
          <span {...childProps}>{children}</span>
        )
      ) : (
        <span {...childProps}>{children}</span>
      )}
    </OverlayTrigger>
  );
};

export default ChildrensTooltip;
